import type { OptionsDocument, QuoteArea, QuoteDocument } from 'paintscout'
import type { UpdateableLineItem } from '../../line-items'
import { getLineItem } from '../../line-items'
import { getArea } from '../../areas'
import type { UpdateableArea } from '../../areas'
import type { UpdateableAreaSubstrate } from '../../area-substrates'
import { getAreaSubstrate } from '../../area-substrates'
import type { BulkEditDetails } from '../../bulk-update'
import { getSubstrate } from '../../substrates'

// Get detials for bulk edit form given quote and keys
export function generateBulkEditDetails(args: {
  quote: QuoteDocument
  options: OptionsDocument
  keys: string[]
}): BulkEditDetails {
  const { quote, options, keys } = args
  const editItems = []

  // Get editItems from each key selected
  keys.forEach((key) => {
    //if line-item
    if (Object.keys(quote.lineItems).includes(key)) {
      const type = 'line-item'
      const lineItem = getLineItem({ quote, key })
      const lineItemMaterials = lineItem?.materials?.length > 0 ? [...lineItem.materials] : ['']
      const lineItemName = lineItem.label
      const lineItemRate = getLineItemRate(lineItem)

      editItems.push({ type, name: lineItemName, rate: lineItemRate, products: lineItemMaterials, lineItem })
    }

    // if area
    if (Object.keys(quote.areas).includes(key)) {
      const quoteArea = quote.areas[key]

      const area = getArea({ quote, options, key })
      const itemType = 'substrate'
      const name = area.label

      // Grab all UpdateableAreaSubstrates
      Object.keys(quoteArea.substrates).forEach((subKey) => {
        if (quoteArea.substrates[subKey][0]) {
          quoteArea.substrates[subKey].forEach((substrate, index) => {
            const updateableAreaSubstrate = getAreaSubstrate({
              quote,
              options,
              areaKey: key,
              key: quoteArea.substrates[subKey][index]?.key,
              full: false
            }) as UpdateableAreaSubstrate

            const rate = updateableAreaSubstrate?.rateValue?.useCustom
              ? updateableAreaSubstrate.rateValue.custom
              : updateableAreaSubstrate?.rateValue?.default
            const products = updateableAreaSubstrate?.products.length ? [...updateableAreaSubstrate.products] : ['']

            editItems.push({
              type: itemType,
              name,
              rate: rate ?? null,
              products,
              substrate: updateableAreaSubstrate
            })
          })
        }
      })
    }

    //if group - handle line-items and area within group
    if (Object.keys(quote.groups).includes(key)) {
      // convert a groups children areas into updateableAreaSubstrates
      quote.groups[key].children.forEach((groupAreaKey) => {
        //if group line-item
        if (Object.keys(quote.lineItems).includes(groupAreaKey)) {
          const type = 'line-item'

          const lineItem = getLineItem({ quote, key: groupAreaKey })
          const lineItemMaterials = lineItem?.materials?.length > 0 ? [...lineItem.materials] : ['']
          const lineItemName = lineItem.label
          const lineItemRate = getLineItemRate(lineItem)

          editItems.push({ type, name: lineItemName, rate: lineItemRate, products: lineItemMaterials, lineItem })
        }

        // if group area
        if (Object.keys(quote.areas).includes(groupAreaKey)) {
          const quoteArea = quote.areas[groupAreaKey]
          const area = getArea({ quote, options, key: groupAreaKey })
          const itemType = 'substrate'
          const name = area.label

          // Grab all UpdateableAreaSubstrates
          Object.keys(quoteArea.substrates).forEach((subKey) => {
            if (quoteArea.substrates[subKey][0]) {
              quoteArea.substrates[subKey].forEach((substrate, index) => {
                const updateableAreaSubstrate = getAreaSubstrate({
                  quote,
                  options,
                  areaKey: area.key,
                  key: quoteArea.substrates[subKey][index]?.key,
                  full: false
                }) as UpdateableAreaSubstrate

                const rate = updateableAreaSubstrate?.rateValue?.useCustom
                  ? updateableAreaSubstrate.rateValue.custom
                  : updateableAreaSubstrate?.rateValue?.default
                const products = updateableAreaSubstrate?.products ? [...updateableAreaSubstrate.products] : ['']

                editItems.push({
                  type: itemType,
                  name,
                  rate: rate ?? null,
                  products,
                  substrate: updateableAreaSubstrate
                })
              })
            }
          })
        }
      })
    }

    // if substrate
    if (Object.keys(quote.substrates).includes(key)) {
      const itemType = 'substrate'
      const substrate = getSubstrate({ quote, key })
      const lastIndex = substrate.substrateDescription.default.lastIndexOf('<')
      const firstIndex = substrate.substrateDescription.default.indexOf('>')
      const areaDetailList = [] as { quoteArea: QuoteArea; updateableArea: UpdateableArea }[]

      // Breakup description to find areas it belongs to
      // so we can check each areaSubstrate in it
      substrate.substrateDescription.default
        .slice(firstIndex + 1, lastIndex)
        .split(',')
        .map((label) => {
          Object.keys(quote.areas).forEach((areaKey) => {
            if (
              quote.areas[areaKey].area_label.value === label.trim() ||
              quote.areas[areaKey].area_label.value === label.split('(')[0].trim()
            ) {
              const updateableArea = getArea({ quote, options, key: areaKey })
              const quoteArea = quote.areas[areaKey]
              areaDetailList.push({ quoteArea, updateableArea })
            }
          })
        })
        .filter(Boolean)

      // For each area create the updateableAreaSubstrate if the
      // areaSubstrate has been selected as part of the substrate
      areaDetailList.forEach((areaDetail) => {
        const { quoteArea, updateableArea } = areaDetail

        Object.values(quoteArea.substrates).forEach((substrateValue) => {
          if (substrateValue[0]?.rate?.label === substrate.label) {
            const updateableAreaSubstrate = getAreaSubstrate({
              quote,
              options,
              areaKey: updateableArea.key,
              key: substrateValue[0].key,
              full: false
            }) as UpdateableAreaSubstrate

            const rate = updateableAreaSubstrate?.rateValue?.useCustom
              ? updateableAreaSubstrate.rateValue.custom
              : updateableAreaSubstrate?.rateValue?.default
            const products = updateableAreaSubstrate?.products ? [...updateableAreaSubstrate.products] : ['']
            editItems.push({
              type: itemType,
              name: updateableArea.label,
              rate: rate ?? null,
              products,
              substrate: updateableAreaSubstrate
            })
          }
        })
      })
    }
  })

  // Filter items for unique categories
  let uniqueProducts = []
  const uniqueRates = []

  editItems.forEach((item) => {
    item?.products.forEach((product) => {
      // Handle substrates w/ no product
      if (
        !product ||
        product === '' ||
        product === undefined ||
        product?.label === '' ||
        product?.product?.label === '' ||
        (item.type === 'substrate' && Object.keys(item?.substrate?.product ?? {}).length < 2)
      ) {
        if (!uniqueProducts.includes('No Material Selected')) {
          uniqueProducts.push('No Material Selected')
        }
      }
      // Unique areaSubstrate products
      if (!uniqueProducts.includes(product?.product?.label)) {
        uniqueProducts.push(product.product?.label)
      }
      // Unique lineItem products
      if (!uniqueProducts.includes(product?.label)) {
        uniqueProducts.push(product?.label)
      }
    })
    // Unique rates TODO: delete?
    if (!uniqueRates.includes(item.rate) && item.rate) {
      uniqueRates.push(item.rate)
    }
  })

  // Clear undefined products(duplicates)
  uniqueProducts = uniqueProducts.filter((product) => product)

  const details = {
    editItems,
    uniqueProducts,
    uniqueRates
  }

  return details
}

// Get rate from line item // TODO: Rates/delete
const getLineItemRate = (lineItem: UpdateableLineItem) => {
  if (lineItem.calculate) {
    if (lineItem?.customHourlyRate) {
      // rate based w/ custom
      return lineItem?.hourlyRate ?? null
    } else {
      // Rate based w/ default
      return lineItem.price / lineItem.hours
    }
  } else {
    // Not rate based
    return null
  }
}
